import React, {useState} from 'react';
import {Box, CircularProgress, Paper} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useHarvestSummary} from 'apps/web-portal-ui/src/app/hooks/useHarvestSummary';
import {useMeasurementConversion} from 'apps/web-portal-ui/src/app/hooks/useMeasurementConversion';
import SummaryTable from './SummaryTable';
import ImageViewer from './ImageViewer';
import NoInfoOverlay from '../../../../../components/UI/DashboardPanel/NoInfoOverlay';
import {getDateString} from '../../../../../helpers/dateHelpers';
import {capitalize} from 'lodash';
import {Props} from './types';
import styled from 'styled-components';

const TableContainer = styled(Paper)`
  height: 100%;
  margin-top: 10px;
  width: 50%;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
`;

const Container = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const VisualisationContainer = styled(Box)`
  margin-top: 10px;
  padding-bottom: 1%;
  padding: 2.5%;
  margin-right: 5px;
  width: 50%;
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  background-color: whitesmoke;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
`;

const LoaderContainer = styled(Box)`
  height: 85%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HarvestSummaryPanel = ({user, selectedFarm, activeFarm, selectedPolygon, selectedOrganization, isMobile}: Props) => {
  const {t} = useTranslation();
  const {sugarUnit, convertSugar, round} = useMeasurementConversion();
  const {targetBaume, harvestDate, averageBaume, averagePH, averageTA, maturityImage, loadingHarvestData, imageGenerationDate, seasonEndDate, isCalibrated} = useHarvestSummary(
    selectedOrganization,
    selectedFarm,
    selectedPolygon
  );
  const [zoom, setZoom] = useState(200);
  // if season ended this year and maturity image is calibrated then let user know that maturity image will be available from the next season
  const getFutureImageGenerationDate = (date: Date) => {
    if (!date) return null;
    // increase one year
    const futureDate = new Date(date).setFullYear(new Date(date).getFullYear() + 1);
    return t('dashboard.summary.available.from') + ' ' + getDateString(new Date(futureDate));
  };
  // true if block has future image generation date
  const futureImageGenerationDate = imageGenerationDate && imageGenerationDate > new Date(Date.now());
  const futureImageGenerationDateText = t('dashboard.summary.available.from') + ' ' + getDateString(imageGenerationDate);
  // true if season end is in the future
  const futureSeasonEndDate = seasonEndDate && seasonEndDate > new Date(Date.now());
  const uncalibratedText = t('dashboard.summary.uncalibrated.info');
  const formatMetric = (metric: number | null) => {
    return futureImageGenerationDate ? futureImageGenerationDateText : !isCalibrated ? uncalibratedText : futureSeasonEndDate ? metric : getFutureImageGenerationDate(imageGenerationDate);
  };

  const rows: [string, string | number | null][] = [
    [t('polygon.info.block.name'), selectedPolygon?.name],
    //only if imageGenerationDate is in the past and season end date is in the future.
    [`${t('dashboard.summary.target')} ${capitalize(sugarUnit)}`, !futureImageGenerationDate && futureSeasonEndDate ? convertSugar(targetBaume) : null],
    [`${t('dashboard.summary.average')} ${capitalize(sugarUnit)}`, formatMetric(convertSugar(averageBaume))],
    [`${t('dashboard.summary.average')} pH`, formatMetric(round(averagePH, 2))],
    [`${t('dashboard.summary.average')} TA`, formatMetric(round(averageTA, 2))],
    [t('dashboard.summary.predicted.harvest.date'), futureImageGenerationDate ? futureImageGenerationDateText : (futureSeasonEndDate && getDateString(harvestDate)) || null],
  ];

  // display maturity image - only if imageGenerationDate is in the past and season end date is in the future.
  return (
    <Container>
      <TableContainer elevation={1} variant="outlined" square>
        <SummaryTable rows={rows} beforeImageGenerationDate />
      </TableContainer>
      <VisualisationContainer>
        {loadingHarvestData ? (
          <LoaderContainer>
            <CircularProgress size={60} />
          </LoaderContainer>
        ) : maturityImage && !futureImageGenerationDate && futureSeasonEndDate ? (
          <ImageViewer src={maturityImage} title={t('dashboard.current.average.baume')} zoom={zoom} onZoomChange={setZoom} />
        ) : (
          <NoInfoOverlay name={t('dashboard.summary.visualisation')} />
        )}
      </VisualisationContainer>
    </Container>
  );
};

export default React.memo(HarvestSummaryPanel);
